// src/hooks/roles/useRoles.ts
import { useState, useCallback } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { getRoles, createRole, deleteRole , updateRole} from "../../../services/roleService";
import type { Role, RoleCreatePayload } from "../../../types/roleTypes";
import { Pagination } from "types/messageTypes";

type FetchOptions = { silent?: boolean };

export const useRoles = () => {
  const { t } = useTranslation();

  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [infoPagination, setInfoPagination] = useState<any>({
    page: 1,
    limit: 10,
    total: 0,
  });

  // UI state
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);

  const fetchRoles = useCallback(async (filters: any = {page: 1 , limit: 10}) => {
    try {
      setLoading(true);
      const res = await getRoles(filters);
      setRoles(res?.data || []);
      setInfoPagination(res?.pagination || {});
      setError(null);
    } catch (err) {
      const msg = t("failed_to_fetch_roles");
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [t]);

  const handleCreate = async (payload: RoleCreatePayload) => {
    try {
      await createRole(payload);
      message.success(t("roles.role_created_successfully"));
      await fetchRoles();
      return true;
    } catch (err: any) {
      console.error(err);
      return false;
    }
  };

  const handleDelete = async (roleId: string) => {
    try {
      await deleteRole(roleId);
      message.success(t("roles.role_deleted_successfully"));
      await fetchRoles();
      return true;
    } catch (err: any) {
      console.error(err);
      return false;
    }
  };


  const handleUpdate = async (roleId: string, payload: RoleCreatePayload) => {
    try {
      await updateRole(roleId, payload);
      message.success(t("roles.role_updated_successfully"));
      await fetchRoles();
      return true;
    } catch (err: any) {
      console.error(err);
      return false;
    }
  };



  return {
    roles,
    loading,
    error,
    // ui state
    selectedRole,
    setSelectedRole,
    // actions
    fetchRoles,
    handleCreate,
    handleDelete,
    handleUpdate,

    infoPagination,
    setInfoPagination,
  };
};

export default useRoles;
